"use client";

import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  Stack,
  type SelectChangeEvent,
  InputAdornment,
  Checkbox,
  ListItemText,
  Select,
  OutlinedInput,
  FormHelperText,
  FormControl,
  LinearProgress,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router";
import { useThoughtCoCreate } from "@/contexts/ThoughtCoCreateContext";
import { useAlert } from "@/contexts/AlertContext";
import { useUser } from "@/contexts/UserContext";
import { apiRemote } from "@/api";
import { marked } from "marked";
import { StyledLink } from "@/components/StyledLink";

const dropdownStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "divider",
    },
  },
};

const TECH_RESOURCES = [
  "Computers",
  "Tablets",
  "Smart Boards",
  "VR Equipment",
  "Coding Labs",
];
const KEY_CHALLENGES = [
  "Limited Budget",
  "Outdated Curriculum",
  "Teacher Training",
  "Student Engagement",
  "Technology Integration",
  "Special Education Needs",
];

const convertTextToHTML = (text: string) => {
  return text
    .replace(/\n\n### (.*?)\n\n/g, "<h3>$1</h3>") // Chuyển đổi tiêu đề cấp 3
    .replace(/\n\n(\d+)\. \*\*(.*?)\*\*:\n/g, "<h4>$1. $2</h4>") // Chuyển đổi tiêu đề cấp 4
    .replace(/\n   - (.*?)/g, "<li>$1</li>") // Chuyển đổi danh sách
    .replace(/\n\n/g, "<p>") // Xuống dòng thành đoạn mới
    .replace(/\n/g, "<br>"); // Xuống dòng lẻ thành <br>
};

const STATUS_DRAFT = process.env.STATUS_DRAFT;
const STATUS_SUBMITTED = process.env.STATUS_SUBMITTED;
const STATUS_NOT_STARTED = process.env.STATUS_NOT_STARTED;
const STATUS_IN_PROGRESS = process.env.STATUS_IN_PROGRESS;
const STATUS_ENDED = process.env.STATUS_ENDED;

export default function Deliverable2() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchTech, setSearchTech] = useState("");
  const [searchChallenges, setSearchChallenges] = useState("");
  const [selectedTech, setSelectedTech] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string[]>([]);
  const [problem, setProblem] = useState("");
  const [usedCount, setUsedCount] = useState(0);
  const [maxLimit, setMaxLimit] = useState(6);
  const [isGenerating, setIsGenerating] = useState(false);
  const navigate = useNavigate();

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const { userRoundParts, loading, loadUserRound1, aiUsage } =
    useThoughtCoCreate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const alert = useAlert();
  const { user } = useUser();

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const localStorageKey = `draft_deliverable2_${user?.user_login}_${id}`;
  const [isCleared, setIsCleared] = useState(false);

  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem(localStorageKey);
    return savedData
      ? JSON.parse(savedData)
      : {
          schoolType: "",
          location: "",
          studentPopulation: "",
          teacherCount: "",
          techResources: "",
          keyChallenges: "",
          additionalContext: "",
          aiPrompt: "",
          aiEngine: "",
          aiFinalProposal: "",
          aiTool: "",
        };
  });

  useEffect(() => {
    if (!isCleared) {
      localStorage.setItem(localStorageKey, JSON.stringify(formData));
      if (formData.techResources) {
        setSelectedTech(formData.techResources.split(","));
      }
      if (formData.keyChallenges) {
        setSelectedKey(formData.keyChallenges.split(","));
      }
    }
    
  }, [formData]);

  const validateForm = () => {
    let newErrors: { [key: string]: string } = {};

    if (!formData.schoolType) newErrors.schoolType = "Required";
    if (!formData.location) newErrors.location = "Required";
    if (!formData.studentPopulation) newErrors.studentPopulation = "Required";
    if (!formData.teacherCount) newErrors.teacherCount = "Required";
    if (selectedTech.length === 0) newErrors.techResources = "Required";
    if (selectedKey.length === 0) newErrors.keyChallenges = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const current_round = Number(id) - 1;
    const previous_round = Number(id) - 2;
    if (loading || !userRoundParts?.[current_round]) return;
    if (aiUsage) {
      setUsedCount(aiUsage.used_count);
      setMaxLimit(aiUsage.max_limit);
    }
    const roundData = userRoundParts[current_round];
    const previousRoundData = userRoundParts[previous_round];
    if (roundData.status == Number(STATUS_SUBMITTED)) {
      setIsSubmitted(true);
    } else {
      setIsSubmitted(false);
    }
    if (roundData.submission_data) {
      const submissionData = JSON.parse(roundData.submission_data);
      setFormData(submissionData);
      setSelectedTech(submissionData.techResources.split(","));
      setSelectedKey(submissionData.keyChallenges.split(","));
    }
    if (previousRoundData.submission_data) {
      const submissionData = JSON.parse(previousRoundData.submission_data);
      setProblem(submissionData.problem);
    }
    // console.log(userRound, id, userRound[Number(id)].due_date);
    const dueDate = new Date(roundData.due_date);
    const today = new Date();
    // console.log(today, dueDate)
    const dueDateOnly = new Date(
      dueDate.getFullYear(),
      dueDate.getMonth(),
      dueDate.getDate()
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    if (
      previousRoundData &&
      previousRoundData.status !== Number(STATUS_DRAFT) &&
      previousRoundData.status !== Number(STATUS_SUBMITTED)
    ) {
      setStatusMessage(
        `You need to complete the previous part to work on this one.`
      );
    } else {
      // if (
      //   todayOnly < dueDateOnly &&
      //   roundData.status !== Number(STATUS_SUBMITTED)
      // ) {
      //   setStatusMessage(
      //     `Submission is not open yet. Due date: ${roundData.due_date}`
      //   );
      // } else
      if (
        todayOnly > dueDateOnly &&
        roundData.status !== Number(STATUS_SUBMITTED)
      ) {
        setStatusMessage(
          `Submission is overdue. Final deadline: ${roundData.due_date}`
        );
        setIsDeadlinePassed(true);
      } else if (
        todayOnly > dueDateOnly
      ) {
        setIsDeadlinePassed(true);
      } else {
        setStatusMessage(null); // Within the allowed time, no message needed
        setIsDeadlinePassed(false);
      }
    }
  }, [id, userRoundParts, loading]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    
    if(event.target.name == "aiEngine"){
      
      setFormData({
        ...formData,
        ["aiTool"]: "OpenAI",
        [event.target.name]: event.target.value,
      });
      setErrors({
        ...errors,
        [event.target.name]: "",
      });
      // if(formData.aiEngine) {
      //   setFormData({
      //     ...formData,
      //     [event.target.name]: "OpenAI",
      //   });
      //   setErrors({
      //     ...errors,
      //     [event.target.name]: "",
      //   });
      // }else{
      //   setFormData({
      //     ...formData,
      //     [event.target.name]: event.target.value,
      //   });
      //   setErrors({
      //     ...errors,
      //     [event.target.name]: "",
      //   });
      // }
    }else{
      setFormData({
        ...formData,
        [event.target.name]: event.target.value,
      });
      setErrors({
        ...errors,
        [event.target.name]: "",
      });
    }
    
  };

  const handleChangeTechResource = (
    event: SelectChangeEvent<typeof selectedTech>
  ) => {
    const {
      target: { value },
    } = event;
    const selectedValues = typeof value === "string" ? value.split(",") : value;
    setSelectedTech(selectedValues);
    setFormData({
      ...formData,
      techResources: selectedValues.toString(),
    });
    setErrors({
      ...errors,
      techResources: "",
    });
  };

  const filteredResources = TECH_RESOURCES.filter((resource) =>
    resource.toLowerCase().includes(searchTech.toLowerCase())
  );

  const handleChangeKeyChallenge = (
    event: SelectChangeEvent<typeof selectedKey>
  ) => {
    const {
      target: { value },
    } = event;
    const selectedValues = typeof value === "string" ? value.split(",") : value;
    if (selectedValues.length <= 3) {
      setSelectedKey(selectedValues);
      setFormData({
        ...formData,
        keyChallenges: selectedValues.toString(),
      });
      setErrors({
        ...errors,
        keyChallenges: "",
      });
    }
  };

  const filteredKeys = KEY_CHALLENGES.filter((challenge) =>
    challenge.toLowerCase().includes(searchChallenges.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeadlinePassed || isSubmitting) return;

    if (validateForm()) {
      if (!formData.aiFinalProposal) {
        alert.error("Please generate proposal first");
        return;
      }
      setIsSubmitting(true);
      try {
        apiRemote
          .postStep({
            user: user?.user_login || "",
            round_part_id: Number(id),
            status: 2,
            submission_data: JSON.stringify(formData),
            step: 1,
          })
          .then((data) => {
            console.log(data);
            if (data.code == 200) {
              alert.success(data.message);
              loadUserRound1();
              setIsSubmitted(true);
              setIsCleared(true);
              localStorage.removeItem(localStorageKey);
            } else {
              alert.error(data.message);
            }
          })
          .finally(() => setIsSubmitting(false));
      } catch (error) {
        alert.error("Something was wrong!");
        console.log(error);
      }
      console.log("Form submitted successfully", formData);
    } else {
      console.log("Form has errors", errors);
    }
    // Handle submission logic here
  };

  const handleSaveDraft = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeadlinePassed || isSubmitting) return;

    setIsSubmitting(true);

    if (validateForm()) {
      if (!formData.aiFinalProposal) {
        alert.error("Please generate proposal first");
        return;
      }
      try {
        apiRemote
          .postStep({
            user: user?.user_login || "",
            round_part_id: Number(id),
            status: 1,
            submission_data: JSON.stringify(formData),
            step: 1,
          })
          .then((data) => {
            console.log(data);
            if (data.code == 200) {
              alert.success(data.message);
              loadUserRound1();
              setIsSubmitted(true);
              // setIsCleared(true);
              localStorage.removeItem(localStorageKey);
            } else {
              alert.error(data.message);
            }
          })
          .finally(() => setIsSubmitting(false));
      } catch (error) {
        alert.error("Something was wrong!");
        console.log(error);
      }
      console.log("Form submitted successfully", formData);
    } else {
      console.log("Form has errors", errors);
    }
  };

  const handleGenerateAi = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeadlinePassed) return;

    if (usedCount >= maxLimit) return;
    // console.log(formData);
    if (validateForm()) {
      if (!formData.aiEngine) {
        setErrors({
          ...errors,
          aiEngine: "Required",
        });
        return;
      } else {
        setErrors({
          ...errors,
          aiEngine: "",
        });
        setIsGenerating(true);
        try {
          const data = await apiRemote.generateAi({
            problem: problem,
            school_type: formData.schoolType,
            student_population: formData.studentPopulation,
            location: formData.location,
            ai_engine: formData.aiEngine,
            teacher_count: formData.teacherCount,
            key_challenge: formData.keyChallenges,
            tech_resources: formData.techResources,
            additional_context: formData.additionalContext,
            user: user?.user_login || "",
            step: 2,
            prompt: formData.aiPrompt,
          });

          if (data.code == 200) {
            alert.success(data.message);
            setFormData({
              ...formData,
              aiFinalProposal: data.data,
            });
            loadUserRound1();
            setIsSubmitted(true);
          } else {
            alert.error(data.message);
          }
        } catch (error) {
          alert.error("An error occurred while generating the proposal");
        } finally {
          setIsGenerating(false);
        }
      }
    } else {
      console.log("Form has errors", errors);
    }
    // Handle submission logic here
  };

  const handleNext = () => {
    const nextId = Number(id) + 1;
    navigate(`../${nextId}`);
  };

  const handlePrevious = () => {
    const previousId = Number(id) - 1;
    navigate(`../${previousId}`);
  };

  return (
    <>
      {statusMessage ? (
        <Typography
          variant="body1"
          color={statusMessage.includes("overdue") ? "error" : "warning"}
          sx={{ mb: 2 }}
        >
          {statusMessage}
        </Typography>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              School Background
            </Typography>
            <Stack spacing={3}>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    School Type
                  </Typography>
                  <TextField
                    select
                    label="Select school type"
                    name="schoolType"
                    value={formData.schoolType}
                    onChange={handleChange}
                    fullWidth
                    sx={dropdownStyle}
                    error={!!errors.schoolType} // Hiển thị viền đỏ nếu có lỗi
                    helperText={errors.schoolType} // Hiển thị lỗi bên dưới
                    InputProps={{ readOnly: isDeadlinePassed }}
                  >
                    <MenuItem value="Primary">Primary</MenuItem>
                    <MenuItem value="Secondary">Secondary</MenuItem>
                    <MenuItem value="High School">High School</MenuItem>
                    <MenuItem value="College">College</MenuItem>
                  </TextField>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    Location
                  </Typography>
                  <TextField
                    select
                    label="Select location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    fullWidth
                    sx={dropdownStyle}
                    error={!!errors.location} // Hiển thị viền đỏ nếu có lỗi
                    helperText={errors.location} // Hiển thị lỗi bên dưới
                    InputProps={{ readOnly: isDeadlinePassed }}
                  >
                    <MenuItem value="urban">Urban</MenuItem>
                    <MenuItem value="suburban">Suburban</MenuItem>
                    <MenuItem value="rural">Rural</MenuItem>
                  </TextField>
                </Box>
              </Box>

              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    Student Population
                  </Typography>
                  <TextField
                    select
                    label="Select student population"
                    name="studentPopulation"
                    value={formData.studentPopulation}
                    onChange={handleChange}
                    fullWidth
                    sx={dropdownStyle}
                    error={!!errors.studentPopulation} // Hiển thị viền đỏ nếu có lỗi
                    helperText={errors.studentPopulation} // Hiển thị lỗi bên dưới
                    InputProps={{ readOnly: isDeadlinePassed }}
                  >
                    <MenuItem value="Less than 200">{"<200"}</MenuItem>
                    <MenuItem value="200-500">200-500</MenuItem>
                    <MenuItem value="501-1000">501-1000</MenuItem>
                    <MenuItem value="More than 1000">{">1000"}</MenuItem>
                  </TextField>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    Teacher Count
                  </Typography>
                  <TextField
                    select
                    label="Select teacher count"
                    name="teacherCount"
                    value={formData.teacherCount}
                    onChange={handleChange}
                    fullWidth
                    sx={dropdownStyle}
                    error={!!errors.teacherCount} // Hiển thị viền đỏ nếu có lỗi
                    helperText={errors.teacherCount} // Hiển thị lỗi bên dưới
                    InputProps={{ readOnly: isDeadlinePassed }}
                  >
                    <MenuItem value="Less than 10">{"<20"}</MenuItem>
                    <MenuItem value="10-30">10-30</MenuItem>
                    <MenuItem value="31-50">31-50</MenuItem>
                    <MenuItem value="More than 50">{">50"}</MenuItem>
                  </TextField>
                </Box>
              </Box>

              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    Existing Tech Resources
                  </Typography>
                  <FormControl fullWidth error={!!errors.techResources}>
                    <Select
                      multiple
                      fullWidth
                      value={selectedTech}
                      labelId="tech-resources-label"
                      onChange={handleChangeTechResource}
                      input={<OutlinedInput />}
                      renderValue={(selected) => `${selected.length} selected`}
                      sx={{
                        "& .MuiSelect-select": {
                          p: 1.5,
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                            width: 400,
                            mt: 0.5,
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          position: "sticky",
                          top: 0,
                          bgcolor: "background.paper",
                          zIndex: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Search..."
                          value={searchTech}
                          onChange={(e) => setSearchTech(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      {filteredResources.map((resource) => (
                        <MenuItem
                          key={resource}
                          value={resource}
                          sx={{ py: 0 }}
                        >
                          <Checkbox
                            checked={selectedTech.indexOf(resource) > -1}
                          />
                          <ListItemText primary={resource} />
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.techResources && (
                      <FormHelperText>{errors.techResources}</FormHelperText>
                    )}
                  </FormControl>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    Key Challenges (up to 3)
                  </Typography>
                  <FormControl fullWidth error={!!errors.keyChallenges}>
                    <Select
                      multiple
                      fullWidth
                      labelId="key-challenges-label"
                      value={selectedKey}
                      onChange={handleChangeKeyChallenge}
                      input={<OutlinedInput />}
                      renderValue={(selected) => `${selected.length} selected`}
                      sx={{
                        "& .MuiSelect-select": {
                          p: 1.5,
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                            width: 400,
                            mt: 0.5,
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          position: "sticky",
                          top: 0,
                          bgcolor: "background.paper",
                          zIndex: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Search..."
                          value={searchChallenges}
                          onChange={(e) => setSearchChallenges(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      {filteredKeys.map((key) => (
                        <MenuItem key={key} value={key} sx={{ py: 0 }}>
                          <Checkbox checked={selectedKey.indexOf(key) > -1} />
                          <ListItemText primary={key} />
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.keyChallenges && (
                      <FormHelperText>{errors.keyChallenges}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    fontWeight: 500,
                  }}
                >
                  Additional Context (Optional)
                </Typography>
                <TextField
                  label="Provide any additional context about your school..."
                  name="additionalContext"
                  value={formData.additionalContext}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Provide any additional context about your school..."
                  sx={dropdownStyle}
                  InputProps={{ readOnly: isDeadlinePassed }}
                />
              </Box>
            </Stack>
          </Paper>

          {/* AI Generation Panel */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              AI Generation Panel
            </Typography>
            <Stack spacing={3}>
              <TextField
                label="AI Prompt"
                name="aiPrompt"
                value={formData.aiPrompt}
                onChange={handleChange}
                multiline
                rows={4}
                fullWidth
                placeholder="Enter your prompt for AI generation..."
                sx={dropdownStyle}
              />

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    fontWeight: 500,
                  }}
                >
                  AI Engine
                </Typography>
                <TextField
                  select
                  label="Select AI engine"
                  name="aiEngine"
                  value={formData.aiEngine}
                  onChange={handleChange}
                  fullWidth
                  sx={dropdownStyle}
                  error={!!errors.aiEngine} // Hiển thị viền đỏ nếu có lỗi
                  helperText={errors.aiEngine}
                  InputProps={{ readOnly: isDeadlinePassed }}
                >
                  <MenuItem value="1">OpenAI</MenuItem>
                </TextField>
              </Box>

              <Button
                variant="contained"
                sx={{
                  bgcolor: "black",
                  color: "white",
                  alignSelf: "flex-start",
                  "&:hover": {
                    bgcolor: "grey.900",
                  },
                }}
                onClick={handleGenerateAi}
                disabled={isGenerating || isDeadlinePassed}
              >
                Generate Proposal ({maxLimit - usedCount} attempts left)
              </Button>
              {isGenerating && (
                <Box sx={{ width: "100%" }}>
                  <LinearProgress />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Generating proposal...
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Final Submission */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Final Submission
            </Typography>
            <Stack spacing={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 500,
                }}
              >
                Final Proposal
              </Typography>
              <div
                dangerouslySetInnerHTML={{
                  __html: marked(
                    formData.aiFinalProposal || formData.finalProposal || ""
                  ),
                }}
              />
              {/* <TextField
                label="Enter your final proposal"
                name="finalProposal"
                value={formData.finalProposal}
                onChange={handleChange}
                multiline
                disabled
                rows={6}
                fullWidth
                placeholder="Enter your final proposal here..."
                sx={dropdownStyle}
              /> */}

              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 500,
                }}
              >
                Which AI tool did you use (if any)?
              </Typography>
              <TextField
                label="Enter the AI tool you used, or 'None' if you didn't use any"
                name="aiTool"
                value={formData.aiTool}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
                placeholder="Enter the AI tool you used..."
                sx={dropdownStyle}
                InputProps={{ readOnly: isDeadlinePassed }}
              />
            </Stack>
          </Paper>

          {/* Action Buttons */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            sx={{
              justifyContent: "space-between",
              mt: 3,
              width: "100%",
            }}
          >
            <Button
              variant="text"
              color="inherit"
              sx={{
                color: "text.primary",
                mb: isMobile ? 2 : 0,
                alignSelf: isMobile ? "flex-start" : "auto",
              }}
            >
              <StyledLink to={`../../dashboard`} style={{ color: "#000" }}>
                Back to Dashboard
              </StyledLink>
            </Button>
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={isMobile ? 1 : 2}
              sx={{ width: isMobile ? "100%" : "auto", alignItems: "center" }}
            >
              {isSubmitting && <CircularProgress size={20} sx={{ ml: 1, verticalAlign: "middle" }} />}
              {/* <Button
                variant="text"
                fullWidth={isMobile}
                sx={{
                  mr: 2,
                  color: "text.primary",
                }}
                onClick={handleSaveDraft}
                disabled={isSubmitted || isSubmitting}
              >
                Save as Draft
              </Button> */}
              <Button
                variant="contained"
                fullWidth={isMobile}
                sx={{
                  bgcolor: "black",
                  "&:hover": {
                    bgcolor: "grey.900",
                  },
                }}
                onClick={handleSubmit}
                disabled={isDeadlinePassed || isSubmitting}
              >
                Submit D2
              </Button>
              {isSubmitted && (
                <>
                  <Button
                    variant="contained"
                    fullWidth={isMobile}
                    sx={{
                      ml: 2,
                      bgcolor: "blue",
                      "&:hover": {
                        bgcolor: "darkblue",
                      },
                    }}
                    onClick={handlePrevious}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth={isMobile}
                    sx={{
                      ml: 2,
                      bgcolor: "blue",
                      "&:hover": {
                        bgcolor: "darkblue",
                      },
                    }}
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </>
  );
}
